import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './RelatedAds.styles';
import { Ad } from '@/api/types/common.types';
import { AdCard } from '@/components/AdCard/AdCard';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { Button } from '@/components/UI/Button/Button';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useToastStore } from '@/store/useToastStore';
import { useResponsive } from '@/hooks/useResponsive';
import { CollectionMasterModal, CollectionMasterSheet } from '@/features';
import { Toast } from '@/components/UI/Toast';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';

interface Props {
  isLoading: boolean;
  title?: string;
  ads?: Ad[];
  showMoreText?: string;
  onShowMore?: () => void;
  chunkSize: AdsChunkSize;
  hasBottomButton?: boolean;
  transitionSource?: TransitionSourceBlockEnum;
}

export const RelatedAds: FC<Props> = ({
  title,
  isLoading,
  ads,
  showMoreText,
  onShowMore,
  chunkSize,
  hasBottomButton,
  transitionSource,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const { openToast, closeToast, onAnimationComplete } = useToastStore();

  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

  const handleAddToFavorites = (targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.toast_tip'),
        action: t('bookmark_collections.toast_action'),
        onActionClick: handleToastActionClick,
      });
    }, 50);
  };

  const handleRemoveFromFavorites = (targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.unbookmarked'),
      });
    }, 50);
  };

  const handleToastActionClick = () => {
    closeToast();
    setIsCollectionMasterOpen(true);
  };

  return (
    <>
      <S.Container data-testid={CommonTestIds.adsSection.section}>
        {title && (
          <S.Title data-testid={CommonTestIds.adsSection.title}>
            {isLoading ? <SkeletonLoader width="125px" height="18px" /> : title}
          </S.Title>
        )}
        <S.AdCardsBox data-testid={CommonTestIds.adCards.cardsBox}>
          {isLoading
            ? Array.from({ length: chunkSize }, (_, index) => (
                <AdCard key={index} isLoading />
              ))
            : ads?.map((ad, idx) => {
                const key = `${ad.id}_${idx}`;
                return (
                  <AdCard
                    key={key}
                    ad={ad}
                    onAddToFavorites={handleAddToFavorites}
                    onRemoveFromFavorites={handleRemoveFromFavorites}
                    transitionSource={transitionSource}
                  />
                );
              })}
        </S.AdCardsBox>
        {hasBottomButton && (
          <S.MoreBottomBox>
            {isLoading ? (
              <SkeletonLoader borderRadius={7} height="50px" />
            ) : (
              <Button
                data-testid={CommonTestIds.adsSection.showMoreButton}
                appearance="secondaryWhite"
                onClick={onShowMore}
              >
                {showMoreText ?? t('common.show_all')}
              </Button>
            )}
          </S.MoreBottomBox>
        )}
      </S.Container>

      {isDesktop ? (
        <CollectionMasterModal
          isOpen={isCollectionMasterOpen}
          onClose={() => setIsCollectionMasterOpen(false)}
        />
      ) : (
        <CollectionMasterSheet
          isOpen={isCollectionMasterOpen}
          onClose={() => setIsCollectionMasterOpen(false)}
        />
      )}

      <Toast />
    </>
  );
};
